import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, switchMap, catchError, tap } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { UsuarioOnlineLocalizacao } from '../interfaces/UsuarioOnlineLocalizacao';
import { AuthService } from '../../../services/auth.service';

export interface RegisterRequestDto {
    page?: number;
    pageSize?: number;
    search?: string;
}

export interface UsuarioResponseDto {
    usuarioId: string;
    nome: string;
    email?: string;
    empresa?: string;
    empresaId?: string;
    nomeDaEmpresa?: string;
    numeroMatricula?: string;
    cpf?: string;
    horaEntrada?: string;
    horaSaida?: string;
    latitudeAtual?: string;
    longitudeAtual?: string;
    dataHoraUltimaAutenticacao?: string;
    fotoUrl?: string;
    isOnline?: boolean;
    role?: string;
}

export interface CriarVistoriaResponseDto {
    itens?: Array<any>;
    totalItens?: number;
    paginaAtual?: number;
    totalPaginas?: number;
    tamanhoPagina?: number;
    id?: string;
    usuarioId?: string;
    vistorias?: Array<any>;
    totalVistorias?: number;
    tempoMedio?: string;
}

@Injectable({ providedIn: 'root' })
export class UsuarioService {
    private colaboradoresCache: UsuarioResponseDto[] = [];
    private ultimaAtualizacao = 0;
    private tempoExpiracaoCache = 5 * 60 * 1000;

    constructor(
        private httpClient: HttpClient,
        private router: Router,
        private injector: Injector
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                if (!event.url.includes('/admin')) {
                    this.limparCache();
                }
            }
        });

        setTimeout(() => {
            this.injector.get(AuthService).onLogout().subscribe(() => {
                this.limparCache();
            });
        });
    }

    private processarUrlFoto(fotoUrl: string | undefined | null): string {
        if (!fotoUrl) return 'assets/images/user-default.png';
        return fotoUrl.startsWith('http') ? fotoUrl : `${environment.mediaUrl}${fotoUrl}`;
    }

    private formatarEndereco(usuario: UsuarioResponseDto): string {
        if (usuario?.latitudeAtual && usuario?.longitudeAtual) {
            return `Lat: ${usuario.latitudeAtual.substring(0, 8)}, Long: ${usuario.longitudeAtual.substring(0, 8)}`;
        }
        return 'Localização não disponível';
    }

    carregarTodosColaboradoresPaginado(page: number = 1, pageSize: number = 10, search: string = '', filterType: string = 'nome', sortColumn: string = '', sortDirection: string = 'asc'): Observable<{ colaboradores: any[], total: number }> {
        const agora = Date.now();
        const estaEmAdmin = this.router.url.includes('/admin');

        if (estaEmAdmin && this.colaboradoresCache.length > 0 && (agora - this.ultimaAtualizacao) < this.tempoExpiracaoCache) {
            return of({ colaboradores: this.colaboradoresCache, total: this.colaboradoresCache.length });
        }

        const params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());

        return this.httpClient.get<any>(`${environment.controllApp}/usuario/tecnicos`, { headers: this.getHeaders(), params }).pipe(
            map((response: any) => {

                let colaboradoresData: UsuarioResponseDto[] = [];
                if (response?.itens && Array.isArray(response.itens)) {
                    colaboradoresData = response.itens;
                } else if (response?.items && Array.isArray(response.items)) {
                    colaboradoresData = response.items;
                } else if (Array.isArray(response)) {
                    colaboradoresData = response;
                } else {
                    return { colaboradores: [], total: 0 };
                }

                const colaboradoresProcessados = colaboradoresData
                    .filter((usuario: UsuarioResponseDto) => usuario.role?.toLowerCase() === 'colaborador')
                    .map((usuario: UsuarioResponseDto) => ({
                        id: usuario.usuarioId,
                        usuarioId: usuario.usuarioId,
                        nome: usuario.nome || 'Sem nome',
                        email: usuario.email || '-',
                        cpf: usuario.cpf || '-',
                        empresaId: usuario.empresaId,
                        empresa: usuario.empresa,
                        nomeDaEmpresa: usuario.nomeDaEmpresa || '-', // Removido usuario.nomeDaEmpresa
                        numeroMatricula: usuario.numeroMatricula || '-',
                        matricula: usuario.numeroMatricula || '-',
                        fotoUrl: this.processarUrlFoto(usuario.fotoUrl),
                        isOnline: usuario.isOnline || false,
                        latitudeAtual: usuario.latitudeAtual,
                        longitudeAtual: usuario.longitudeAtual,
                        role: usuario.role
                    }));

                this.colaboradoresCache = colaboradoresProcessados;
                this.ultimaAtualizacao = agora;

                const total = response.totalItens || response.total || colaboradoresData.length;

                return { colaboradores: colaboradoresProcessados, total };
            }),
            switchMap((result: { colaboradores: any[], total: number }) => {
                const empresaRequests = result.colaboradores.map((colaborador: any) =>
                    colaborador.empresaId ? this.buscarNomeEmpresa(colaborador.empresaId).pipe(
                        map(empresa => ({ ...colaborador, empresaNome: empresa.nomeDaEmpresa || 'N/A' })),
                        catchError(() => of({ ...colaborador, empresaNome: 'N/A' }))
                    ) : of({ ...colaborador, empresaNome: 'N/A' })
                );
                return forkJoin(empresaRequests).pipe(
                    map(colaboradoresComEmpresa => ({ colaboradores: colaboradoresComEmpresa, total: result.total }))
                );
            })
        );
    }

    carregarTodosColaboradores(): Observable<any[]> {
        const agora = Date.now();
        const estaEmAdmin = this.router.url.includes('/admin');

        if (estaEmAdmin && this.colaboradoresCache.length > 0 && (agora - this.ultimaAtualizacao) < this.tempoExpiracaoCache) {
            return of(this.colaboradoresCache);
        }

        return this.httpClient.get<any>(`${environment.controllApp}/usuario/todos-os-tecnicos`, { headers: this.getHeaders() }).pipe(
            map((response: any) => {
                let colaboradoresData: UsuarioResponseDto[] = [];
                if (response?.itens && Array.isArray(response.itens)) {
                    colaboradoresData = response.itens;
                } else if (Array.isArray(response)) {
                    colaboradoresData = response;
                } else {
                    return [];
                }

                const colaboradoresProcessados = colaboradoresData
                    .filter((usuario: UsuarioResponseDto) => usuario.role?.toLowerCase() === 'colaborador')
                    .map((usuario: UsuarioResponseDto) => ({
                        id: usuario.usuarioId,
                        usuarioId: usuario.usuarioId,
                        nome: usuario.nome || 'Sem nome',
                        email: usuario.email || '-',
                        cpf: usuario.cpf || '-',
                        empresaId: usuario.empresaId,
                        empresa: usuario.empresa,
                        nomeDaEmpresa: usuario.nomeDaEmpresa || '-',
                        numeroMatricula: usuario.numeroMatricula || '-',
                        matricula: usuario.numeroMatricula || '-',
                        fotoUrl: this.processarUrlFoto(usuario.fotoUrl),
                        isOnline: usuario.isOnline || false,
                        latitudeAtual: usuario.latitudeAtual,
                        longitudeAtual: usuario.longitudeAtual,
                        role: usuario.role
                    }));

                this.colaboradoresCache = colaboradoresProcessados;
                this.ultimaAtualizacao = agora;

                return colaboradoresProcessados;
            }),
            switchMap((colaboradores: any[]) => {
                const empresaRequests = colaboradores.map((colaborador: any) =>
                    colaborador.empresaId ? this.buscarNomeEmpresa(colaborador.empresaId).pipe(
                        map(empresa => ({ ...colaborador, empresaNome: empresa.nomeDaEmpresa || 'N/A' })),
                        catchError(() => of({ ...colaborador, empresaNome: 'N/A' }))
                    ) : of({ ...colaborador, empresaNome: 'N/A' })
                );
                return forkJoin(empresaRequests);
            })
        );
    }

    limparCache(): void {
        if (!this.router.url.includes('/admin')) {
            this.colaboradoresCache = [];
            this.ultimaAtualizacao = 0;
        }
    }

    buscarNomeEmpresa(empresaId: string): Observable<any> {
        if (!empresaId) return of({ nomeDaEmpresa: 'N/A' });
        return this.httpClient.get<any>(`${environment.controllApp}/usuario/empresa/${empresaId}`, { headers: this.getHeaders() }).pipe(
            catchError(() => of({ nomeDaEmpresa: 'N/A' }))
        );
    }

    private getToken(): string {
        return localStorage.getItem('token') || '';
    }

    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.getToken()}`
        });
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = 'Ocorreu um erro na requisição';
        if (error.error instanceof ErrorEvent) {
            errorMessage = `Erro: ${error.error.message}`;
        } else {
            errorMessage = `Código: ${error.status}, Mensagem: ${error.message}`;
        }
        console.error(errorMessage);
        return throwError(() => new Error(errorMessage));
    }

    getStatistics(dataInicio: string, dataFim: string): Observable<any> {
        const url = `${environment.cedaeapp}/buscasdetalhadas/totais-por-periodo`;
        return this.httpClient.get<any>(url, {
            headers: this.getHeaders(),
            params: { dataInicio, dataFim }
        }).pipe(
            catchError(error => {
                console.error('❌ Erro na chamada de estatísticas:', error);
                return of({ totalVistorias: 0, totalSolicitacoes: 0, totalMarcacoes: 0 });
            })
        );
    }

    getStatisticsUsuarios(pagina = 1, tamanhoPagina = 10): Observable<any> {
        return this.httpClient.get<any>(
            `${environment.cedaeapp}/buscasdetalhadas/usuarios/online/metricas`,
            {
                headers: this.getHeaders(),
                params: { Pagina: pagina, TamanhoPagina: tamanhoPagina }
            }
        ).pipe(catchError(error => this.handleError(error)));
    }

    getVistoriasByUsuarioIdAndDate(usuarioId: string, dataInicial: string, dataFinal: string): Observable<CriarVistoriaResponseDto> {
        const url = `${environment.cedaeapp}/vistoria/obterporidusuario/${usuarioId}`;
        let params = new HttpParams().set('dataInicial', dataInicial).set('dataFinal', dataFinal);
        return this.httpClient.get<CriarVistoriaResponseDto>(url, {
            headers: this.getHeaders(),
            params: params
        }).pipe(
            tap(response => {
                if (response.itens && Array.isArray(response.itens)) {

                }
            }),
            catchError((err: HttpErrorResponse) => {
                console.error(`❌ Erro ao consultar vistoria para usuário ${usuarioId}:`, err);
                return of({ itens: [], totalItens: 0, totalVistorias: 0, tempoMedio: 'N/A' });
            })
        );
    }

    timeStringToMinutes(timeStr: string): number {
        if (timeStr.includes('.')) timeStr = timeStr.split('.')[0];
        const timeParts = timeStr.split(':').map(Number);
        if (timeParts.length === 3) return timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2];
        if (timeParts.length === 2) return timeParts[0] * 60 + timeParts[1];
        return 0;
    }

    minutesToTimeString(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
        const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    }

    calculateAverageTime(vistorias: any[]): string {
        if (!vistorias || vistorias.length === 0) return '00:00:00';
        let totalSeconds = 0;
        let countWithTime = 0;

        vistorias.forEach(vistoria => {
            if (vistoria.dataHoraInicio && vistoria.dataHoraFim) {
                const inicio = new Date(vistoria.dataHoraInicio).getTime();
                const fim = new Date(vistoria.dataHoraFim).getTime();
                if (inicio && fim && fim > inicio) {
                    totalSeconds += (fim - inicio) / 1000;
                    countWithTime++;
                }
            } else if (vistoria.tempoTotal) {
                totalSeconds += this.timeStringToMinutes(vistoria.tempoTotal);
                countWithTime++;
            }
        });

        if (countWithTime === 0) return '00:00:00';
        const avgSeconds = Math.floor(totalSeconds / countWithTime);
        return this.minutesToTimeString(avgSeconds);
    }

    getUsuariosOnlineLocalizacao(): Observable<UsuarioOnlineLocalizacao[]> {
        return this.httpClient.get<UsuarioOnlineLocalizacao[]>(
            `${environment.cedaeapp}/buscasdetalhadas/usuarios/online/localizacao`,
            { headers: this.getHeaders() }
        ).pipe(
            map(usuarios => usuarios.map(usuario => ({
                ...usuario,
                fotoUrl: usuario.fotoUrl
                    ? `${environment.apiBaseUrl}${usuario.fotoUrl}`
                    : 'assets/images/user-default.png'
            }))),
            catchError(error => this.handleError(error))
        );
    }

    getUsuarioById(usuarioId: string): Observable<UsuarioResponseDto> {
        const url = `${environment.controllApp}/usuario/${usuarioId}`;
        return this.httpClient.get<UsuarioResponseDto>(url, {
            headers: this.getHeaders()
        }).pipe(
            catchError(error => this.handleError(error))
        );
    }
}